import { useCallback } from "react";
import { useSelector } from "react-redux";

import {
  DEFAULT_COLOR,
  IS_FORECAST_COMPARE_CHART,
  JITTER_PLOT_LEGEND_ITEMS_SERIES_NAME
} from "constants/chart.constants";
import { EvaChart, EvaStatisticsKind } from "constants/charts.enums";
import { useChartsContext } from "contexts";
import {
  isStatusKeyedResponse,
  wellStatusDisplayName
} from "entities/charts/eva/requests/wellStatus";
import { isLegendVisibleSeries } from "entities/charts/eva/translators/constants";
import { getEffectiveProductLineStyle } from "entities/charts/eva/translators/getEffectiveProductLineStyle";
import { getEffectiveSeriesColor } from "entities/charts/eva/translators/getEffectiveSeriesColor";
import {
  isRigFracValueSeries,
  resolveRigFracSeriesColor,
  resolveSeriesColorIgnoringCount
} from "entities/charts/eva/translators/rigFracActivity";
import { isSplitYearResponseFresh } from "entities/charts/eva/translators/splitYear";
import { RootState } from "store/rootReducer";
import { isAllStatusesRequest } from "types";
import { seriesMatchesProduct } from "utils/chart/seriesMatchesProduct";
import { normalizeMatchKey } from "utils/color/focusColorSync/operatorKeys";
import { removeCount } from "utils/removeCountFromTitle";

import {
  useChartDependencies,
  useChartEntities,
  useRigFracSeriesColors
} from "hooks/charts";
import { useWellStatusSeriesColorMap } from "hooks/charts/useWellStatusLegendOrder";

import { LegendItemModel } from "models";
import { EntityKind } from "models/entityKind";
import { ChartSeries } from "models/model";

import { createLegendItem } from "components/charts/utils";
import { useScreenshotContext } from "components/screenshot/hooks";
import { applyAbbreviations } from "components/screenshot/utils";
import { useUserSettings } from "components/user/hooks";

import { getWellContributionLegendItems } from "../utils/getWellContributionLegendItems";
import useLineOfBestFitLegendItems from "./useLineOfBestFitLegendItems";
import useSlopeLegendItem from "./useSlopeLegendItems";
import useStatisticsLegendItem from "./useStatisticsLegendItem";
import useTypeWellLegendItems from "./useTypeWellLegendItems";

const getFilteredItems = (series: ChartSeries[]): ChartSeries[] => {
  return series.filter(
    (series) =>
      !["Average", "P10", "P50", "P90"].includes(series.groupTitle) &&
      !series.isForecast &&
      !series.isSecondaryYAxis &&
      !series.label?.includes(" Fit - ")
  );
};

const getTotalRateDateLegendItems = (
  series: ChartSeries[],
  showWellCount: boolean,
  entityKind: EntityKind
): LegendItemModel[] => {
  if (!series) {
    return [];
  }

  return (
    series
      .filter((item) => !item.isForecast)
      .filter((item) => showWellCount || item.label !== "Well Count")
      // EVA-6819: in multi-product mode the backend emits one summed count plus one per
      // product; only the summed one belongs in the legend. Per-product counts are
      // data-table only. See isLegendVisibleSeries.
      .filter((item) => isLegendVisibleSeries(item.label, item.groupTitle))
      .map((item) => {
        const legendItem = createLegendItem(
          item.label,
          true,
          item.style?.hexColor ?? DEFAULT_COLOR //default to black if there's no color
        );
        legendItem.count = 1;
        if (item.label === "Well Count") {
          // since series is coming directly from apiResponse, label for "facility count" is still "well count"
          if (entityKind === EntityKind.Facility) {
            legendItem.title = "Facility Count";
          }
          legendItem.color = DEFAULT_COLOR;
        }
        return legendItem;
      })
  );
};

/**
 * A simple hook to get the legend items for the chart
 *
 * @returns list of legend items
 */
const useChartLegendItems = (): LegendItemModel[] => {
  // custom hooks
  const avgLegendItem = useStatisticsLegendItem(EvaStatisticsKind.Average);
  const p90LegendItem = useStatisticsLegendItem(EvaStatisticsKind.P90);
  const p50LegendItem = useStatisticsLegendItem(EvaStatisticsKind.P50);
  const p10LegendItem = useStatisticsLegendItem(EvaStatisticsKind.P10);

  const slopeLegendItem = useSlopeLegendItem();

  const typeWellLegendItems = useTypeWellLegendItems();
  const lineOfBestFitLegendItems = useLineOfBestFitLegendItems();
  const { userAbbreviations } = useUserSettings();
  const screenshotState = useScreenshotContext();

  // state from store
  const placeholderBinsEnabled = useSelector(
    (state: RootState) => state.groupBy.placeholderBinsEnabled
  );

  const activeEntities = useSelector((state: RootState) => state.app.activeEntityKinds);

  const { id: chartId, entityKind } = useChartsContext();
  const {
    chartFocus,
    chartType,
    screenshot,
    apiResponse,
    showWellCount,
    jitterPlotToggle,
    multiProduct,
    splitYear,
    histogramToggle,
    request
  } = useChartEntities(chartId);
  // Called unconditionally, alongside the other hooks above — this hook runs
  // for every chart type, and it's a cheap no-op (frozen empty result) for
  // anything that isn't a rig/frac chart. Reads the chartType/request already
  // pulled from useChartEntities above rather than a chartId, so this never
  // opens a second subscription for the same data.
  // Same labels, filtered the same way, as the bars pass in
  // useRigFracChartOptions: a swatch and the bar it labels have to come out of
  // one assignment, including for a label the value domain does not list.
  const rigFracSeriesLabels = (apiResponse?.series ?? [])
    .filter(isRigFracValueSeries)
    .map((s) => s.label);
  const { colors: rigFracSeriesColors } = useRigFracSeriesColors({
    chartType,
    request,
    seriesLabels: rigFracSeriesLabels
  });
  // Same bin → colour map the Well Status bars paint from, so a swatch and the
  // bar it labels come out of one assignment. Called unconditionally alongside
  // the rig/frac equivalent above and for the same reason: it is a cheap memo
  // over the map legend the hook already depends on, not a second subscription.
  // Handed the same chart focus the bars pass, or a swatch and its bar would
  // disagree the moment the user picks a palette.
  const wellStatusSeriesColors = useWellStatusSeriesColorMap(chartFocus);
  const { showGroupsNotInFilter, txnId } = useChartDependencies(entityKind);

  // "Show every status" is an explicit flag on the Well Status request, never a
  // magic `status` value — read it from the request that produced the response,
  // since the two modes come back in shapes the response alone cannot tell apart.
  const isAllStatusesMode = isAllStatusesRequest(request);

  const abbreviations = userAbbreviations?.abbreviations ?? [];

  // derived state
  const { id, legend } =
    entityKind === "Well" || chartType === EvaChart.WellContribution
      ? { id: txnId.id, legend: txnId.legend }
      : {
          id: txnId.facilityId,
          legend: txnId.facilityLegend
        };
  const { legendItems } = legend;

  const isWellEntitiesActive =
    activeEntities.includes(EntityKind.Well) || entityKind === EntityKind.Facility;

  const hasLegendItems =
    Boolean(id) && Boolean(legendItems.length) && isWellEntitiesActive;

  const createAndFormatLegendItem = useCallback(
    (title: string, inFilter: boolean, color: string): LegendItemModel => {
      let formattedTitle = title;

      // Apply abbreviations if screenshot is active and setting is enabled
      if (screenshot?.active && screenshotState?.settings?.applyAbbreviations) {
        formattedTitle = applyAbbreviations(formattedTitle, abbreviations);
      }
      return createLegendItem(formattedTitle, inFilter, color);
    },
    [
      screenshot?.active,
      screenshotState?.settings?.applyAbbreviations,
      screenshotState?.settings?.legendShowTypeWellRescat,
      abbreviations
    ]
  );

  // Rig/Frac charts (EVA-76) have no wells transaction legend, so they would
  // otherwise fall through to the empty "No entities were found" state. Build
  // legend items straight from the response series (Activity vs Time: one per
  // bar group + the overlay line; Recent Activity: one per group). Colours go
  // through the same client-side resolver the bars use (falling back to the
  // server's colour when it's inactive), so a legend swatch always matches the
  // bar it labels — this is the third read site of style.hexColor, alongside
  // the two translators.
  if (
    chartType === EvaChart.RigFracActivity ||
    chartType === EvaChart.RigFracRecentActivity
  ) {
    return (apiResponse?.series ?? []).map((series) =>
      // count stays UNSET (not 0 — a 0 count hides the item entirely): the
      // rig/frac legends show no count badges, per design.
      createAndFormatLegendItem(
        series.label,
        true,
        resolveRigFracSeriesColor(
          series.label,
          rigFracSeriesColors,
          series.style?.hexColor ?? DEFAULT_COLOR
        )
      )
    );
  }

  // Well Status Current in ALL STATUSES mode. The bars are stacked one segment per
  // STATUS across the focus bins, so the map's bin legend describes a grouping the
  // chart is no longer showing — the swatches name the wrong thing entirely. Build
  // from the series instead, which is the same source (and the same colours) the
  // tooltip already lists.
  //
  // Colours go through the SAME resolver the bars do, not straight from
  // `style.hexColor`.
  //
  // The usual case is that the lookup misses by construction — the series are
  // keyed by status while the bin→colour map is keyed by focus bin — and both
  // sides then fall back to the server palette, which is what these swatches
  // should show. But when the active group-by is itself status-like, the bin
  // titles ARE status labels, the lookup HITS for the bars (they call
  // `resolveSeriesColorIgnoringCount`) and missed here, so the swatch and the bar
  // it labels disagreed. A legend that names the wrong colour is worse than
  // either colour on its own.
  // FRESHNESS. `isAllStatusesMode` reads the entity request, which the selector
  // dispatches the instant the user picks All Statuses — with no response, since
  // the fetch has not returned. Until it does, `apiResponse` still holds the
  // previous filtered-mode payload, one series per focus BIN. Building from it
  // would map bin titles through `wellStatusDisplayName`, which falls back to its
  // input, so the legend would list "Montney" and "Duvernay" under a "Status"
  // heading — every entry disabled, so hovering a legitimate bin name does
  // nothing. Same hazard the split-year branch below guards, arriving by the same
  // route: a mode flag that flips ahead of the data it describes.
  //
  // Asked as "do these series look like statuses" rather than by threading the
  // producing request through, because the entity holds only one request and the
  // echo overwrites it. `some`, not `every`: a status the display-name map has
  // not been taught yet should still render alongside the ones it has, rather
  // than dropping the whole legend back to bins.
  const seriesAreStatuses = isStatusKeyedResponse(apiResponse?.series);

  if (
    chartType === EvaChart.WellStatusCurrent &&
    isAllStatusesMode &&
    seriesAreStatuses
  ) {
    return (
      (apiResponse?.series ?? [])
        // Non-zero only (product): a status no well in the filter holds would add a
        // swatch for a segment that draws nothing.
        .filter((series) => (series.y ?? []).some((value) => Number(value) > 0))
        .map((series) => {
          const item = createAndFormatLegendItem(
            // The readable name, matching the bars and the tooltip — the wire value
            // is stage-prefixed ("06. PROD").
            wellStatusDisplayName(String(series.label ?? "")),
            true,
            resolveSeriesColorIgnoringCount(
              series.label,
              wellStatusSeriesColors,
              series.style?.hexColor ?? DEFAULT_COLOR
            )
          );
          // No count badge (the bar lengths carry the numbers, per the rig/frac
          // legends), and NOT hoverable: legend hover highlights the map bin whose
          // title matches, and a status name matches no bin. Left enabled it would be
          // a control that silently does nothing on every hover.
          item.disabled = true;
          return item;
        })
    );
  }

  // Backend-driven Split Year (EVA-40): when chart-svc reshapes the response into
  // quarter/month sub-series (Q1, Q2, ... or Jan, Feb, ...), `apiResponse.series` already
  // has the correct labels/colors — no reshape needed here, just map them directly. The
  // legend must reflect that instead of the well/facility Group By colors (which describe
  // a pre-reshape chart that no longer matches what's plotted), so this is checked first
  // and takes priority over chartFocus/multiProduct/hasLegendItems. Falls through to the
  // normal legend when the backend reports `splitYearApplied: false` (no-op), mirroring
  // the chart's own fallback so the legend and the chart never disagree. Also requires
  // the freshness check (see isSplitYearResponseFresh) so that when the toggle flips off,
  // this doesn't keep reading the stale, still-reshaped `apiResponse` until the debounced
  // fetch resolves — that would show a split-year legend after the chart already fell back.
  const isSplitYearApplied =
    (chartType === EvaChart.StackedBar || chartType === EvaChart.StackedBarMidstream) &&
    splitYear?.active &&
    isSplitYearResponseFresh(request, splitYear.active, histogramToggle?.active) &&
    apiResponse?.splitYearApplied;
  if (isSplitYearApplied) {
    const splitYearLegendItems = (apiResponse?.series ?? []).map((series) =>
      createAndFormatLegendItem(
        series.label,
        true,
        series.style?.hexColor ?? DEFAULT_COLOR
      )
    );
    return [
      ...p10LegendItem,
      ...avgLegendItem,
      ...p50LegendItem,
      ...p90LegendItem,
      ...slopeLegendItem,
      ...splitYearLegendItems,
      ...typeWellLegendItems,
      ...lineOfBestFitLegendItems
    ];
  }

  if (!hasLegendItems) return [...avgLegendItem, ...typeWellLegendItems];
  // const chart = getChart(chartType as ChartTypeLabels);
  if (chartFocus?.active || multiProduct?.active) {
    if (!apiResponse?.series?.length) {
      return [...typeWellLegendItems];
    }
    switch (chartType) {
      case EvaChart.Pie: {
        const itemColors = apiResponse.series[0].style.itemColors;
        const filteredLegendItems = apiResponse.series[0].y.map((title, index) => {
          return createAndFormatLegendItem(
            String(title),
            true,
            String(itemColors[index])
          );
        });
        return [...filteredLegendItems, ...typeWellLegendItems];
      }
      case EvaChart.Mosaic:
      case EvaChart.MosaicMidstream: {
        const filteredLegendItems = apiResponse.series
          .filter(
            (series) =>
              series.groupTitle != "Average" &&
              !series.isForecast &&
              !series.isSecondaryYAxis
          )
          .map((series) => {
            const title = series.groupTitle ? series.groupTitle : series.label;
            return {
              title: removeCount(title),
              hexColor: series.style.hexColor
            };
          })
          .filter(
            (value, index, array) =>
              array.map((a) => a.title).indexOf(value.title) === index
          )
          .map((series) => {
            return createAndFormatLegendItem(series.title, true, series.hexColor);
          });
        return [...filteredLegendItems, ...typeWellLegendItems];
      }
      case EvaChart.BoxPlot:
      case EvaChart.BoxPlotMidstream: {
        if (jitterPlotToggle?.active) {
          const jitterPlotSeries = apiResponse.series.find(
            (series) => series.label === JITTER_PLOT_LEGEND_ITEMS_SERIES_NAME
          );
          if (!jitterPlotSeries) {
            return [];
          }
          const itemColors = jitterPlotSeries.style.itemColors;
          const filteredLegendItems = jitterPlotSeries.x.map((title, index) => {
            return createAndFormatLegendItem(
              String(title),
              true,
              String(itemColors[index])
            );
          });
          return [...filteredLegendItems, ...typeWellLegendItems];
        }
        const filteredLegendItems = getFilteredItems(apiResponse.series)
          .filter(
            (value, index, array) =>
              array.map((a) => a.label).indexOf(value.label) === index
          )
          .map((series) => {
            return createAndFormatLegendItem(series.label, true, series.style.hexColor);
          });
        return [...filteredLegendItems, ...typeWellLegendItems];
      }
      case EvaChart.TotalRateDate: {
        // getCustomLegendItems needs to be called for the total rate date chart because
        // that function adds the well count series to the legend
        break;
      }
      case EvaChart.TotalRateDateMidstream: {
        break;
      }
      case EvaChart.WellContribution: {
        return getWellContributionLegendItems(apiResponse?.series, legendItems, true);
      }
      case EvaChart.CrossPlot:
      case EvaChart.CrossPlotMidstream: {
        const uniqueLegendItemSeries = getFilteredItems(apiResponse.series).reduce(
          (accumulator, currentSeries) => {
            const isLegendItemDuplicate = accumulator.some(
              (item) =>
                (item.label && item.label === currentSeries.label) ||
                (item.groupTitle && item.groupTitle === currentSeries.groupTitle)
            );

            if (!isLegendItemDuplicate) {
              accumulator.push(currentSeries);
            }

            return accumulator;
          },
          []
        );

        const formattedLegendItems = uniqueLegendItemSeries
          .filter((series) => {
            const title = series.groupTitle || series.label;
            return !title.includes("Line of Best Fit");
          })
          .map((series) => {
            const title = series.groupTitle || series.label;
            return createAndFormatLegendItem(title, true, series.style.hexColor);
          });
        return [
          ...formattedLegendItems,
          ...typeWellLegendItems,
          ...lineOfBestFitLegendItems
        ];
      }
      default: {
        const productLineStyles = multiProduct?.properties?.productLineStyles ?? {};
        const allProducts = Object.keys(productLineStyles);
        const uniqueLegendItemSeries = getFilteredItems(apiResponse.series).reduce(
          (accumulator, currentSeries) => {
            const isLegendItemDuplicate = accumulator.some(
              (item) =>
                (item.label && item.label === currentSeries.label) ||
                (item.groupTitle && item.groupTitle === currentSeries.groupTitle)
            );

            if (!isLegendItemDuplicate) {
              accumulator.push(currentSeries);
            }

            return accumulator;
          },
          []
        );
        const formattedLegendItems = uniqueLegendItemSeries.map((series) => {
          const title = series.groupTitle || series.label;
          const legendItem = createAndFormatLegendItem(
            title,
            true,
            getEffectiveSeriesColor(series, multiProduct)
          );
          if (multiProduct?.active) {
            const matchedProduct = allProducts.find((prod) =>
              seriesMatchesProduct(series, prod)
            );
            if (matchedProduct) {
              legendItem.lineStyle = getEffectiveProductLineStyle(
                matchedProduct,
                multiProduct,
                productLineStyles[matchedProduct]
              );
            }
          }
          return legendItem;
        });

        // In multiProduct mode, statistics appear in formattedLegendItems with per-product line styles
        // suppress the standalone square-swatch items to avoid duplication.
        const statisticsItems = multiProduct?.active
          ? []
          : [...p10LegendItem, ...avgLegendItem, ...p50LegendItem, ...p90LegendItem];

        return [
          ...statisticsItems,
          ...formattedLegendItems,
          ...typeWellLegendItems,
          ...slopeLegendItem,
          ...lineOfBestFitLegendItems
        ];
      }
    }
  }

  //custom legendItems allows the chart to override the legend that is returned from the txnId
  const customLegendItems = [];
  if (
    chartType === EvaChart.TotalRateDate ||
    chartType === EvaChart.TotalRateDateMidstream
  ) {
    customLegendItems.push(
      ...getTotalRateDateLegendItems(apiResponse?.series, showWellCount, entityKind)
    );
  } else if (chartType === EvaChart.WellContribution) {
    customLegendItems.push(
      ...getWellContributionLegendItems(apiResponse?.series, legendItems, false)
    );
  }

  // Groups actually plotted in the chart (excludes statistics/forecast/secondary
  // axis series). When forecast compare narrows the series — dropping wells — the
  // map-sourced legend items still carry their old counts, so gate count-based
  // items on actually being plotted. Scoped to forecast-compare charts only:
  // box/cross/pie/mosaic legends don't map 1:1 to plotted series and would be
  // wrongly filtered (e.g. jitter legend collapsing to just the n/a bin). (EVA-6575)
  const plottedGroupKeys = new Set(
    getFilteredItems(apiResponse?.series ?? []).map((series) =>
      removeCount(series.groupTitle || series.label)
    )
  );
  const gateOnPlottedSeries =
    IS_FORECAST_COMPARE_CHART(chartType) && plottedGroupKeys.size > 0;

  // Both Well Status charts plot one series per focus bin, and the aggregation
  // drops every bin that holds none of the counted statuses — so the map-sourced
  // legend lists bins the chart never drew. Gate on the plotted bins here too.
  // Keyed through normalizeMatchKey because the two sources differ in case and
  // spacing, which would silently empty this legend.
  const wellStatusLegendKey = (item: { groupTitle?: string; title?: string }) =>
    normalizeMatchKey(removeCount(item.groupTitle || item.title));
  const plottedWellStatusKeys = new Set(
    [...plottedGroupKeys].map((key) => normalizeMatchKey(key))
  );
  const legendItemsInPlay =
    customLegendItems.length > 0 ? customLegendItems : legendItems;
  // Armed only when the two sources agree on at least one bin. They arrive by
  // INDEPENDENT paths: on a focus change the map legend flips to the new bins at
  // once while the response still holds the previous property's, so no overlap
  // means the response is stale, not that the chart plotted nothing. Gating on it
  // would empty the legend for the whole aggregation, seconds on a large filter,
  // and leave it empty for good if the request then failed. Same staleness guard
  // the two branches above make.
  const gateOnPlottedWellStatusBins =
    (chartType === EvaChart.WellStatusCurrent ||
      chartType === EvaChart.WellStatusOverTime) &&
    legendItemsInPlay.some((item) =>
      plottedWellStatusKeys.has(wellStatusLegendKey(item))
    );

  // legend items in filter
  const filteredLegendItems = legendItemsInPlay
    .filter(
      (item) =>
        item.inFilter &&
        ((placeholderBinsEnabled ? showGroupsNotInFilter : false) ||
          (item.count > 0 &&
            (!gateOnPlottedSeries ||
              plottedGroupKeys.has(removeCount(item.groupTitle || item.title))) &&
            (!gateOnPlottedWellStatusBins ||
              plottedWellStatusKeys.has(wellStatusLegendKey(item)))) ||
          chartType === EvaChart.WellContribution) // Well Contribution chart will always have an undefined count for the legend items
    )
    .map((item) => {
      const title = item.groupTitle ? item.groupTitle : item.title;

      return createAndFormatLegendItem(title, item.inFilter, item.color);
    });

  return [
    ...p10LegendItem,
    ...avgLegendItem,
    ...p50LegendItem,
    ...p90LegendItem,
    ...slopeLegendItem,
    ...filteredLegendItems,
    ...typeWellLegendItems,
    ...lineOfBestFitLegendItems
  ];
};

export default useChartLegendItems;
